import { supabase } from '../lib/supabaseClient';

export interface PreloadedBanner {
  id: string;
  category_id: string;
  name: string;
  display_name: string;
  description: string;
  file_path: string;
  file_url: string;
  file_size: number;
  mime_type: string;
  dimensions: { width: number; height: number };
  tags: string[];
  is_active: boolean;
  sort_order: number;
  local_path: string;
  created_at: string;
  updated_at: string;
}

export class PreloadedBannerService {
  private static cache: Map<string, string> = new Map(); // local_path -> file_url

  /**
   * Get Supabase URL for a local banner path
   */
  static async getSupabaseUrlForLocalPath(localPath: string): Promise<string | null> {
    try {
      console.log(`🚨 BANNER_EXPORT_DEBUG - getSupabaseUrlForLocalPath called with: ${localPath}`);
      console.log(`🚨 BANNER_EXPORT_DEBUG - Cache size: ${this.cache.size}`);
      console.log(`🚨 BANNER_EXPORT_DEBUG - Cache contents:`, Array.from(this.cache.entries()));
      
      // Check cache first
      if (this.cache.has(localPath)) {
        console.log(`🔍 Cache hit for local path: ${localPath}`);
        return this.cache.get(localPath) || null;
      }

      console.log(`🔍 Converting local path to storage URL: ${localPath}`);

      // Convert local path to storage path
      // localPath format: /icons/banner/shop-events/general/filename.png OR /icons/banner/shop-events/filename.png
      // storage path format: preloaded/shop-events/general/filename.png
      if (localPath.startsWith('/icons/banner/')) {
        let storagePath = localPath.replace('/icons/banner/', 'preloaded/');
        
        // Handle legacy paths that are missing the subdirectory
        // Only add /general/ if the path has exactly 3 parts (category/filename.png)
        // and it's one of the categories that should have a subdirectory
        if (storagePath.match(/^preloaded\/(shop-events|nature)\/[^\/]+\.png$/)) {
          const parts = storagePath.split('/');
          const category = parts[1];
          const filename = parts[2];
          storagePath = `preloaded/${category}/general/${filename}`;
          console.log(`🔍 Fixed legacy path: ${localPath} -> ${storagePath}`);
        }
        
        console.log(`🔍 Storage path: ${storagePath}`);
        
        // Get public URL from storage bucket
        const { data } = supabase.storage
          .from('banners')
          .getPublicUrl(storagePath);
        
        if (data?.publicUrl) {
          console.log(`✅ Generated Supabase storage URL: ${data.publicUrl}`);
          this.cache.set(localPath, data.publicUrl);
          return data.publicUrl;
        }
      }

      console.log(`ℹ️ Could not convert local path to storage URL: ${localPath}`);
      return null;
    } catch (error) {
      console.error(`❌ Error getting Supabase URL for ${localPath}:`, error);
      return null;
    }
  }

  /**
   * Check if a banner path is a preloaded banner
   */
  static async isPreloadedBanner(bannerPath: string): Promise<boolean> {
    try {
      const supabaseUrl = await this.getSupabaseUrlForLocalPath(bannerPath);
      return supabaseUrl !== null;
    } catch (error) {
      console.error(`❌ Error checking if banner is preloaded: ${bannerPath}`, error);
      return false;
    }
  }

  /**
   * Convert local banner paths to Supabase URLs in addonSettings
   */
  static async convertLocalPathsToSupabaseUrls(addonSettings: any): Promise<any> {
    try {
      console.log('🔍 Converting local banner paths to Supabase URLs...');

      const convertedSettings = { ...addonSettings };

      // Process banner addons
      if (addonSettings.banner && Array.isArray(addonSettings.banner)) {
        for (let i = 0; i < addonSettings.banner.length; i++) {
          const banner = addonSettings.banner[i];
          
          if (banner.imageUrl && banner.imageUrl.startsWith('/icons/banner/')) {
            console.log(`🔍 Checking banner ${i + 1}: ${banner.imageUrl}`);
            
            const supabaseUrl = await this.getSupabaseUrlForLocalPath(banner.imageUrl);
            if (supabaseUrl) {
              console.log(`✅ Converting to Supabase URL: ${supabaseUrl}`);
              convertedSettings.banner[i] = {
                ...banner,
                imageUrl: supabaseUrl
              };
            } else {
              console.log(`ℹ️ Keeping local path (not a preloaded banner): ${banner.imageUrl}`);
            }
          }
        }
      }

      // Process banner_house addons
      if (addonSettings.banner_house && Array.isArray(addonSettings.banner_house)) {
        for (let i = 0; i < addonSettings.banner_house.length; i++) {
          const bannerHouse = addonSettings.banner_house[i];
          
          if (bannerHouse.selectedBanner && bannerHouse.selectedBanner.startsWith('/icons/banner/')) {
            console.log(`🔍 Checking banner_house ${i + 1}: ${bannerHouse.selectedBanner}`);
            
            const supabaseUrl = await this.getSupabaseUrlForLocalPath(bannerHouse.selectedBanner);
            if (supabaseUrl) {
              console.log(`✅ Converting to Supabase URL: ${supabaseUrl}`);
              convertedSettings.banner_house[i] = {
                ...bannerHouse,
                selectedBanner: supabaseUrl
              };
            } else {
              console.log(`ℹ️ Keeping local path (not a preloaded banner): ${bannerHouse.selectedBanner}`);
            }
          }
        }
      }

      console.log('✅ Local path conversion completed');
      return convertedSettings;
    } catch (error) {
      console.error('❌ Error converting local paths:', error);
      return addonSettings; // Return original if conversion fails
    }
  }

  /**
   * Clear the cache
   */
  static clearCache(): void {
    this.cache.clear();
    console.log('🧹 Preloaded banner cache cleared');
  }

  /**
   * Preload cache with all banners from storage bucket
   */
  static async preloadCache(): Promise<void> {
    try {
      console.log('🔄 Preloading banner cache from storage bucket...');
      console.log('🚨 BANNER_EXPORT_DEBUG - preloadCache called');

      this.cache.clear();
      
      // List all folders in the preloaded directory
      const { data: folders, error: foldersError } = await supabase.storage
        .from('banners')
        .list('preloaded', {
          limit: 100,
          offset: 0
        });

      if (foldersError) {
        console.error('🚨 BANNER_EXPORT_DEBUG - Error listing storage folders:', foldersError);
        throw foldersError;
      }

      console.log('🚨 BANNER_EXPORT_DEBUG - Storage folders found:', folders?.length || 0);

      if (folders && folders.length > 0) {
        // Process each folder (category)
        for (const folder of folders) {
          if (folder.name && !folder.name.startsWith('.')) {
            console.log(`🚨 BANNER_EXPORT_DEBUG - Processing folder: ${folder.name}`);
            
            // List files in this folder
            const { data: files, error: filesError } = await supabase.storage
              .from('banners')
              .list(`preloaded/${folder.name}`, {
                limit: 1000,
                offset: 0
              });

            if (filesError) {
              console.error(`🚨 BANNER_EXPORT_DEBUG - Error listing files in ${folder.name}:`, filesError);
              continue;
            }

            if (files && files.length > 0) {
              // Process each file in the folder
              for (const file of files) {
                if (file.name && !file.name.startsWith('.')) {
                  // Convert storage path to local path
                  // storage path: public/shop-events/filename.png
                  // local path: /icons/banner/shop-events/filename.png
                  const localPath = `/icons/banner/${folder.name}/${file.name}`;
                  
                  // Get public URL for this file
                  const { data: urlData } = supabase.storage
                    .from('banners')
                    .getPublicUrl(`preloaded/${folder.name}/${file.name}`);
                  
                  if (urlData?.publicUrl) {
                    this.cache.set(localPath, urlData.publicUrl);
                    console.log(`🚨 BANNER_EXPORT_DEBUG - Cached: ${localPath} -> ${urlData.publicUrl}`);
                  }
                }
              }
            }
          }
        }
        
        console.log(`✅ Preloaded ${this.cache.size} banners into cache from storage bucket`);
        console.log('🚨 BANNER_EXPORT_DEBUG - Final cache size:', this.cache.size);
      } else {
        console.log('🚨 BANNER_EXPORT_DEBUG - No folders found in storage bucket');
      }
    } catch (error) {
      console.error('❌ Error preloading cache from storage:', error);
    }
  }

  /**
   * Get all preloaded banners for a category
   */
  static async getBannersByCategory(categoryName: string): Promise<PreloadedBanner[]> {
    try {
      const { data, error } = await supabase
        .from('preloaded_banners')
        .select(`
          *,
          banner_categories!inner(name)
        `)
        .eq('banner_categories.name', categoryName)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error(`❌ Error getting banners for category ${categoryName}:`, error);
      return [];
    }
  }

  /**
   * Get banner statistics
   */
  static async getBannerStats(): Promise<{
    total: number;
    byCategory: Record<string, number>;
    totalSize: number;
  }> {
    try {
      const { data, error } = await supabase
        .from('preloaded_banners')
        .select(`
          file_size,
          banner_categories!inner(name)
        `)
        .eq('is_active', true);

      if (error) {
        throw error;
      }

      const stats = {
        total: data?.length || 0,
        byCategory: {} as Record<string, number>,
        totalSize: 0
      };

      if (data) {
        data.forEach(banner => {
          const categoryName = banner.banner_categories?.name || 'unknown';
          stats.byCategory[categoryName] = (stats.byCategory[categoryName] || 0) + 1;
          stats.totalSize += banner.file_size || 0;
        });
      }

      return stats;
    } catch (error) {
      console.error('❌ Error getting banner stats:', error);
      return { total: 0, byCategory: {}, totalSize: 0 };
    }
  }
}





